import OfflineLogic from "../../logic/OfflineLogic.js";
import Totals from "../../model/crew/Totals.js";
import BigNum from "../../model/math/BigNum.js";
import { Settings } from "../../Settings.js";
import NumberFormatter from "../../util/NumberFormatter.js";
import TimeFormatter from "../../util/TimeFormatter.js";
import Page from "../framework/Page.js";
import View from "../framework/View.js";
import { PageIds } from "../PageIds.js";

export default class OfflinePopupView extends View {
    /**
     * @param {string} parentId 
     * @param {Totals} totals 
     * @param {OfflineLogic} offlineLogic
     */
    constructor(parentId, totals, offlineLogic) {
        super(parentId, PageIds.OFFLINE_POPUP);

        /**
         * @constant
         * @type {Totals}
         */
        this.totals = totals;
        /**
         * @constant
         * @type {OfflineLogic}
         */
        this.offlineLogic = offlineLogic;

        /**
         * @type {HTMLElement}
         */
        this.container = null;

        /**
         * @type {HTMLElement}
         */
        this.offlineTimeInfo = null;
        /**
         * @type {HTMLElement}
         */
        this.prestigeEarnedInfo = null;
        /**
         * @type {HTMLElement}
         */
        this.goldEarnedInfo = null;

        /**
         * @type {HTMLElement}
         */
        this.oreEarnedInfo = null;

        /**
         * @type {HTMLElement}
         */
        this.rubyEarnedInfo = null;

        /**
         * @constant
         * @type {BigNum}
         */
        this.displayedPrestige = new BigNum(-10);
        /**
         * @constant
         * @type {BigNum}
         */
        this.displayedGold = new BigNum(-10);
        /**
         * @constant
         * @type {BigNum}
         */
        this.displayedOre = new BigNum(-10);
        /**
         * @constant
         * @type {BigNum}
         */
        this.displayedRuby = new BigNum(-10);

        /**
         * @constant
         * @type {number}
         */
        this.displayedOfflineSeconds = 0;

        /**
         * @constant
         * @type {BigNum}
         */
        this.working = new BigNum(0);

        /**
         * @constant
         * @type {BigNum}
         */
        this.one = new BigNum(1);
    }

    /**
     * Invoked at startup or after a save has been loaded.
     * @override
     */
    initializeView() {
        const parentElement = Page.getElement(this.parentId);
        const popupElement = Page.createElement('div', parentElement, this.getElementId(), 'popup-window-prestige');

        const container = Page.createElement('div', popupElement, null, null);

        ////////////////////////////////////////
        // Header
        ////////////////////////////////////////

        const header = Page.createElement('div', container, null, 'popup-window-header');
        header.innerHTML = 'Offline Progress';

        const thisRef = this;

        header.onclick = function () {
            thisRef.offlineLogic.claimOfflineEarnings();
            thisRef.setViewVisible(false);
            return false;
        };

        ////////////////////////////////////////
        // Content
        ////////////////////////////////////////

        const sectionBody = Page.createElement('div', container, null, 'menu-section-body');

        this.container = Page.createElement('div', sectionBody, null, null);
        
        // You have offline earnings!
        //
        // Offline time: 00:00:00
        //
        // Earned Prestige: []
        //
        // Earned Gold: []
        //
        // [Claim]

        //////////////////////////////////////////
        // Main Prestige Content
        //////////////////////////////////////////
                
        const prestigeInfo1 = Page.createElement('div', this.container, null, 'offline-info');
        prestigeInfo1.innerHTML = 'You have offline earnings!';

        this.offlineTimeInfo = Page.createElement('div', this.container, null, 'offline-info');
        this.offlineTimeInfo.innerHTML = '';

        this.prestigeEarnedInfo = Page.createElement('div', this.container, null, 'offline-info');
        this.prestigeEarnedInfo.innerHTML = '';

        this.goldEarnedInfo = Page.createElement('div', this.container, null, 'offline-info');
        this.goldEarnedInfo.innerHTML = '';

        this.oreEarnedInfo = Page.createElement('div', this.container, null, 'offline-info');
        this.oreEarnedInfo.innerHTML = '';

        this.rubyEarnedInfo = Page.createElement('div', this.container, null, 'offline-info');
        this.rubyEarnedInfo.innerHTML = '';

        const buttonRow = Page.createElement('div', this.container, null, 'save-popup-button-row');

        const claimButton = Page.createElement('div', buttonRow, 'prestigeButton', 'game-button prestige-button');
        claimButton.style.marginTop = '10px';
        claimButton.innerHTML = 'Claim Earnings';

        claimButton.onclick = function () {
            thisRef.offlineLogic.claimOfflineEarnings();
            thisRef.setViewVisible(false);
            return false;
        };

        this.setInitialVisibility(false);

        super.initializeView();
    }

    /**
     * @private
     * @returns {string}
     */
    getPrestigeRate() {
        return this.getRate(this.totals.prestigePerTurn);
    }

    /**
     * @private
     * @returns {string}
     */
    getGoldRate() {
        return this.getRate(this.totals.goldPerTurn);
    }

    /**
     * @private
     * @returns {string}
     */
    getOreRate() {
        return this.getRate(this.totals.orePerTurn);
    }

    /**
     * @private
     * @returns {string}
     */
    getRubyRate() {
        return this.getRate(this.totals.rubyPerTurn);
    }

    /**
     * @private
     * @param {BigNum} amountPerTurn 
     * @returns {string}
     */
    getRate(amountPerTurn) {

        // Convert this into a:  0.000 / second.

        this.working.copy(amountPerTurn);
        this.working.mulNumber(Settings.time.turnsPerSecond)

        if (this.working.lessThan(this.one)) {
            const value = this.working.toNumber();
            return value.toFixed(4) + ' / second';
        }
        else {
            return NumberFormatter.formatBigNum(this.working) + ' / second';
        }
    }

    /**
     * @override
     */
    updateViewContents() {
        const prestige = this.totals.offlinePrestige;
        const gold = this.totals.offlineGold;
        const ore = this.totals.offlineOre;
        const ruby = this.totals.offlineRuby;
        const offlineSeconds = this.totals.countedOfflineSeconds;

        if (this.displayedOfflineSeconds !== offlineSeconds) {
            this.displayedOfflineSeconds = offlineSeconds;

            this.offlineTimeInfo.innerHTML = 'Offline time: ' + TimeFormatter.formatElapsedTime(offlineSeconds * 1000);
        }

        if (!this.displayedPrestige.equals(prestige)) {
            this.displayedPrestige.copy(prestige);
            this.prestigeEarnedInfo.innerHTML = 'Prestige earned: ' + NumberFormatter.formatBigNum(prestige) + ' (' + this.getPrestigeRate() + ')';
        }

        if (!this.displayedGold.equals(gold)) {
            this.displayedGold.copy(gold);
            this.goldEarnedInfo.innerHTML = 'Gold earned: ' + NumberFormatter.formatBigNum(gold) + ' (' + this.getGoldRate() + ')';
        }

        if (!this.displayedOre.equals(ore)) {
            this.displayedOre.copy(ore);
            this.oreEarnedInfo.innerHTML = 'Ore earned: ' + NumberFormatter.formatBigNum(ore) + ' (' + this.getOreRate() + ')';
        }

        if (!this.displayedRuby.equals(ruby)) {
            this.displayedRuby.copy(ruby);
            this.rubyEarnedInfo.innerHTML = 'Ruby earned: ' + NumberFormatter.formatBigNum(ruby) + ' (' + this.getRubyRate() + ')';
        }
    }

}